import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { apiClient } from '@/lib/api'
import { RealtimeChannel } from '@supabase/supabase-js'

interface AgentStatusChange {
	user_id: string
	status: 'online' | 'away' | 'offline'
	status_message: string | null
	last_active_at: string
}

export function useAgentStatusRealtime(teamId?: string, programId?: string) {
	const queryClient = useQueryClient()

	useEffect(() => {
		if (!teamId || !programId) return

		let channel: RealtimeChannel

		const setupRealtimeSubscription = async () => {
			// Subscribe to agent status changes for the specific program
			channel = supabase
				.channel(`agent-status-${programId}`)
				.on(
					'postgres_changes',
					{
						event: '*',
						schema: 'public',
						table: 'agent_status',
						filter: `program_id=eq.${programId}`,
					},
					(payload) => {
						console.log('Agent status change:', payload)

						// Invalidate relevant queries to refetch data
						queryClient.invalidateQueries({
							queryKey: ['agent-status'],
						})
						queryClient.invalidateQueries({
							queryKey: ['team-agents', teamId],
						})

						// If it's the current user's status, also invalidate their specific query
						if (
							payload.new &&
							typeof payload.new === 'object' &&
							'user_id' in payload.new
						) {
							const statusChange = payload.new as AgentStatusChange
							queryClient.invalidateQueries({
								queryKey: ['agent-status', statusChange.user_id],
							})
						}
					}
				)
				.subscribe((status) => {
					if (status === 'SUBSCRIBED') {
						console.log('Subscribed to agent status changes')
					}
				})
		}

		setupRealtimeSubscription()

		// Cleanup subscription on unmount
		return () => {
			if (channel) {
				supabase.removeChannel(channel)
			}
		}
	}, [teamId, programId, queryClient])
}

// Hook to track user activity and update last_active_at
export function useActivityTracker(userId?: string, programId?: string) {
	useEffect(() => {
		if (!userId || !programId) return

		let activityTimer: NodeJS.Timeout
		let lastActivity = Date.now()

		const updateActivity = async () => {
			const now = Date.now()
			// Only update if more than 1 minute has passed since last update
			if (now - lastActivity > 60000) {
				lastActivity = now

				try {
					// Use the apiClient which properly routes to the backend
					await apiClient(`/api/agent-status/activity?program_id=${programId}`, {
						method: 'PATCH',
					})
				} catch (error) {
					console.error('Error updating activity:', error)
				}
			}
		}

		const handleUserActivity = () => {
			clearTimeout(activityTimer)
			activityTimer = setTimeout(updateActivity, 5000) // Debounce updates
		}

		// Track various user activities
		const events = ['mousedown', 'keydown', 'scroll', 'touchstart']
		events.forEach((event) => {
			document.addEventListener(event, handleUserActivity)
		})

		// Cleanup
		return () => {
			clearTimeout(activityTimer)
			events.forEach((event) => {
				document.removeEventListener(event, handleUserActivity)
			})
		}
	}, [userId, programId])
}

// Hook to listen for new chat messages and conversations
export function useChatRealtimeUpdates(programId?: string) {
	const queryClient = useQueryClient()

	useEffect(() => {
		if (!programId) return

		let conversationsChannel: RealtimeChannel
		let messagesChannel: RealtimeChannel

		const setupSubscriptions = async () => {
			// Subscribe to new conversations
			conversationsChannel = supabase
				.channel(`conversations-${programId}`)
				.on(
					'postgres_changes',
					{
						event: '*',
						schema: 'public',
						table: 'chat_conversations',
						filter: `program_id=eq.${programId}`,
					},
					(payload) => {
						console.log('Conversation change:', payload)

						// Invalidate conversations list
						queryClient.invalidateQueries({
							queryKey: ['chat-conversations', programId],
						})

						// If a conversation was assigned, invalidate agent queries
						if (
							payload.new &&
							typeof payload.new === 'object' &&
							'assigned_agent_id' in payload.new
						) {
							queryClient.invalidateQueries({
								queryKey: ['agent-workload'],
							})
						}
					}
				)
				.subscribe()

			// Subscribe to new messages with program_id filter
			messagesChannel = supabase
				.channel(`messages-${programId}`)
				.on(
					'postgres_changes',
					{
						event: 'INSERT',
						schema: 'public',
						table: 'chat_messages',
						filter: `program_id=eq.${programId}`,
					},
					async (payload) => {
						const message = payload.new as { conversation_id?: string }
						if (!message?.conversation_id) return

						console.log('New message for program:', payload)
						
						// Invalidate conversation messages
						queryClient.invalidateQueries({
							queryKey: ['chat-messages', message.conversation_id],
						})
						
						// Also invalidate the conversations list to update unread counts, last message, etc.
						queryClient.invalidateQueries({
							queryKey: ['chat-conversations', programId],
						})
					}
				)
				.subscribe()
		}

		setupSubscriptions()

		// Cleanup
		return () => {
			if (conversationsChannel) {
				supabase.removeChannel(conversationsChannel)
			}
			if (messagesChannel) {
				supabase.removeChannel(messagesChannel)
			}
		}
	}, [programId, queryClient])
}
